import React from "react";

interface BedsAndBathsFilterProps {
  selectedBedrooms?: string;
  selectedBathrooms?: string;
  useExactMatchBeds?: boolean;
  useExactMatchBaths?: boolean;
  onBedroomsChange?: (value: string) => void;
  onBathroomsChange?: (value: string) => void;
  onExactMatchBedsChange?: (value: boolean) => void;
  onExactMatchBathsChange?: (value: boolean) => void;
  onApply?: () => void;
  hideButtons?: boolean;
  fullWidth?: boolean;
}

export const BedsAndBathsFilter: React.FC<BedsAndBathsFilterProps> = ({
  selectedBedrooms = "",
  selectedBathrooms = "",
  useExactMatchBeds = false,
  useExactMatchBaths = false,
  onBedroomsChange,
  onBathroomsChange,
  onExactMatchBedsChange,
  onExactMatchBathsChange,
  onApply,
  hideButtons = false,
  fullWidth = false,
}) => {
  const [localBedrooms, setLocalBedrooms] = React.useState(selectedBedrooms);
  const [localBathrooms, setLocalBathrooms] = React.useState(selectedBathrooms);
  const [localExactMatchBeds, setLocalExactMatchBeds] =
    React.useState(useExactMatchBeds);
  const [localExactMatchBaths, setLocalExactMatchBaths] =
    React.useState(useExactMatchBaths);

  // Update local state when props change
  React.useEffect(() => {
    setLocalBedrooms(selectedBedrooms);
    setLocalBathrooms(selectedBathrooms);
    setLocalExactMatchBeds(useExactMatchBeds);
    setLocalExactMatchBaths(useExactMatchBaths);
  }, [
    selectedBedrooms,
    selectedBathrooms,
    useExactMatchBeds,
    useExactMatchBaths,
  ]);

  const handleBedroomsClick = (value: string) => {
    if (value === "Any") {
      setLocalBedrooms("");
      onBedroomsChange?.("");
      return;
    }
    // If clicking the same value, deselect it
    if (value === localBedrooms) {
      setLocalBedrooms("");
      onBedroomsChange?.("");
      return;
    }
    setLocalBedrooms(value);
    onBedroomsChange?.(value);
  };

  const handleBathroomsClick = (value: string) => {
    if (value === "Any") {
      setLocalBathrooms("");
      onBathroomsChange?.("");
      return;
    }
    // If clicking the same value, deselect it
    if (value === localBathrooms) {
      setLocalBathrooms("");
      onBathroomsChange?.("");
      return;
    }
    setLocalBathrooms(value);
    onBathroomsChange?.(value);
  };

  const handleExactMatchBedsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLocalExactMatchBeds(e.target.checked);
    onExactMatchBedsChange?.(e.target.checked);
  };

  const handleExactMatchBathsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLocalExactMatchBaths(e.target.checked);
    onExactMatchBathsChange?.(e.target.checked);
  };

  const handleApply = () => {
    onApply?.();
  };

  const getButtonText = (value: string, isExactMatch: boolean) => {
    if (value === "Any") return value;
    const number = value.replace("+", "");
    return isExactMatch ? number : `${number}+`;
  };

  const isSelected = (option: string, selectedValue?: string) => {
    if (option === "Any") return !selectedValue;
    const optionNumber = option.replace("+", "");
    const selectedNumber = selectedValue?.replace("+", "");
    return optionNumber === selectedNumber;
  };

  return (
    <div className={`${fullWidth ? "w-full" : "w-[420px]"} lg:p-4`}>
      {/* Bedrooms */}
      <div className="mb-4">
        <div className="text-sm font-medium text-gray-700 mb-2">
          Number of Bedrooms
        </div>
        <div className="flex w-full border border-gray-200">
          {["Any", "1+", "2+", "3+", "4+", "5+"].map((option, index) => (
            <button
              key={option}
              type="button"
              className={`flex-1 py-1 sm:py-1.5 text-xs sm:text-sm transition-colors rounded-none ${
                isSelected(option, localBedrooms)
                  ? "bg-primary text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              } ${index !== 5 ? "border-r border-gray-200" : ""}`}
              onClick={() => handleBedroomsClick(option)}
            >
              {getButtonText(option, localExactMatchBeds)}
            </button>
          ))}
        </div>
      </div>

      {/* Use exact match checkbox for bedrooms */}
      {/* <div className="mb-4">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={localExactMatchBeds}
            onChange={handleExactMatchBedsChange}
            className="w-4 h-4  border-gray-300 text-primary focus:ring-primary "
          />
          Use exact match for bedrooms
        </label>
      </div> */}

      {/* Bathrooms */}
      <div className="mb-4">
        <div className="text-sm font-medium text-gray-700 mb-2">
          Number of Bathrooms
        </div>
        <div className="flex w-full border border-gray-200">
          {["Any", "1+", "2+", "3+", "4+", "5+"].map((option, index) => (
            <button
              key={option}
              type="button"
              className={`flex-1 py-1 sm:py-1.5 text-xs sm:text-sm transition-colors rounded-none ${
                isSelected(option, localBathrooms)
                  ? "bg-primary text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              } ${index !== 5 ? "border-r border-gray-200" : ""}`}
              onClick={() => handleBathroomsClick(option)}
            >
              {getButtonText(option, localExactMatchBaths)}
            </button>
          ))}
        </div>
      </div>

      {/* Use exact match checkbox for bathrooms */}
      {/* <div className="mb-4">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={localExactMatchBaths}
            onChange={handleExactMatchBathsChange}
            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          Use exact match for bathrooms
        </label>
      </div> */}

      {/* Action Buttons */}
      {!hideButtons && (
        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            type="button"
            onClick={() => {
              setLocalBedrooms("");
              setLocalBathrooms("");
              setLocalExactMatchBeds(false);
              setLocalExactMatchBaths(false);
              onBedroomsChange?.("");
              onBathroomsChange?.("");
              onExactMatchBedsChange?.(false);
              onExactMatchBathsChange?.(false);
            }}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 !bg-transparent"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="px-6 py-2 text-xs text-white rounded-lg"
            style={{ backgroundColor: "var(--primary)" }}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

export default BedsAndBathsFilter;
